import { useContext, useEffect, useRef } from 'react';

import type { MantineSpacing, MantineStyleProps } from '@mantine/core';

import { Flex } from '@/new_src/view/components/Flex';
import { ScrollArea } from '@/new_src/view/components/ScrollArea';
import { Stack } from '@/new_src/view/components/Stack';

import ModalContext from './ModalContext';

import styled from 'styled-components';

type ModalBodySplitProps = {
  mah?: MantineStyleProps['mah'];
  gap?: MantineSpacing;
  shadow?: boolean;
  topContent?: React.ReactNode;
  scrollableContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
};

/**
 * @description
 * 모달 바디 영역 내부에서 스크롤이 가능한 영역, 스크롤이 안되는 영역을 구분하기 위한 컴포넌트
 * topContent: 스크롤이 안되는 상단 영역
 * scrollableContent: 스크롤이 되는 영역
 * bottomContent: 스크롤이 안되는 하단 영역
 */
const ModalBodySplit = ({
  mah,
  gap = 0,
  shadow = true,
  topContent,
  scrollableContent,
  bottomContent,
}: ModalBodySplitProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { showFooterBorder, onHeaderBorderChange, onFooterBorderChange } = useContext(ModalContext);
  const showShadow = showFooterBorder && shadow;

  const handleScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }

    const isOverMaxHeight = el.scrollHeight > el.clientHeight;

    if (!isOverMaxHeight) {
      onHeaderBorderChange(false);
      onFooterBorderChange(false);
      return;
    }

    const isOverScrollTop = el.scrollTop > 0;
    const isOverScrollBottom = el.scrollTop + el.clientHeight < el.scrollHeight;
    onHeaderBorderChange(isOverScrollTop);
    onFooterBorderChange(isOverScrollBottom);
  };

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      // DOM 변경 후 레이아웃 루프 오류 방지를 위해 requestAnimationFrame 내부에서 처리
      requestAnimationFrame(() => {
        const isOverMaxHeight = el.scrollHeight > el.clientHeight;
        const isOverScrollBottom = el.scrollTop + el.clientHeight < el.scrollHeight;

        if (!mah) {
          onFooterBorderChange(isOverMaxHeight && isOverScrollBottom);
        } else {
          for (const entry of entries) {
            const { height } = entry.contentRect;
            if (mah === height) {
              onFooterBorderChange(true);
            }
          }
        }
      });
    });

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Flex p="0 24px 24px 24px" justify="space-between" columnGap={12} overflow="hidden">
      <Stack w="100%" gap={gap}>
        {topContent}
        <ScrollArea pos="relative" viewportRef={scrollAreaRef} w="100%" mah={mah} onScrollPositionChange={handleScroll}>
          {scrollableContent}
          {showShadow && <Shadow />}
        </ScrollArea>
        {bottomContent}
      </Stack>
    </Flex>
  );
};

const Shadow = styled.div`
  position: absolute;
  bottom: 0;
  height: 80px;
  width: 100%;
  pointer-events: none;
  background: linear-gradient(to top, ${({ theme }) => theme.mantineColors.gray[1]} 6.25%, rgba(255, 255, 255, 0) 100%);
  z-index: 1;
`;

export default ModalBodySplit;
